import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './pm-calendar.style.js';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { PMCalendarEvent } from '../../../types/pm.types.js';

@customElement('pm-calendar')
export class PMCalendar extends LitElement {
  static styles = styles;

  @property({ type: String })
  companyId = '';

  @property({ type: Array })
  pmWorkOrders: any[] = [];

  @state()
  private calendar: Calendar | null = null;

  @state()
  private calendarEvents: PMCalendarEvent[] = [];

  connectedCallback() {
    super.connectedCallback();
    // Wait for the next tick to ensure the DOM is ready
    setTimeout(() => {
      this.initializeCalendar();
    }, 0);
    
    // Add resize listener to update calendar size
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.calendar) {
      this.calendar.destroy();
    }
    window.removeEventListener('resize', this.handleResize);
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);
    if (changedProperties.has('pmWorkOrders')) {
      this.updateCalendarEvents();
    }
  }

  private initializeCalendar() {
    const calendarEl = this.shadowRoot?.getElementById('calendar');
    if (!calendarEl) {
      return;
    }

    this.calendar = new Calendar(calendarEl, {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialView: 'dayGridMonth',
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay'
      },
      firstDay: 1, // Start week on Monday (0=Sunday, 1=Monday)
      events: this.calendarEvents,
      eventClick: (info) => this.handleEventClick(info),
      eventDidMount: (info) => this.handleEventMount(info),
      editable: true, // Enable event editing
      eventDrop: (info) => this.handleEventDrop(info),
      eventResize: (info) => this.handleEventResize(info),
      height: '100%',
      // Removed aspectRatio to allow full height
      nowIndicator: true,
      businessHours: {
        daysOfWeek: [1, 2, 3, 4, 5], // Monday - Friday
        startTime: '08:00',
        endTime: '17:00'
      }
    });

    this.calendar.render();

    // Update events if we already have PM templates
    if (this.pmWorkOrders.length > 0) {
      this.updateCalendarEvents();
    }
  }

  private updateCalendarEvents() {
    if (!this.calendar) return;

    this.calendarEvents = this.pmWorkOrders.map(workOrder => {
      if (!workOrder || !workOrder.model) {
        return null;
      }

      // Use pm_due_at for PM work orders, fallback to created_at if not available
      const dueDate = new Date(workOrder.model.pm_due_at || workOrder.model.created_at || Date.now());
      const today = new Date();
      const isOverdue = dueDate < today;
      const isDueToday = dueDate.toDateString() === today.toDateString();
      
      let backgroundColor = '#007bff';
      let borderColor = '#007bff';
      let textColor = '#fff';
      let className = 'upcoming';

      if (isOverdue) {
        backgroundColor = '#dc3545';
        borderColor = '#dc3545';
        className = 'overdue';
      } else if (isDueToday) {
        backgroundColor = '#ffc107';
        borderColor = '#ffc107';
        textColor = '#000';
        className = 'due-today';
      }

      return {
        id: workOrder.id,
        title: workOrder.model.description || 'PM Work Order',
        start: dueDate,
        end: dueDate,
        allDay: true,
        backgroundColor,
        borderColor,
        textColor,
        className,
        extendedProps: {
          work_order_id: workOrder.id,
          asset_id: workOrder.model.asset_id || '',
          priority: workOrder.model.priority_cat || 2,
          status: workOrder.model.status_cat || 1,
          is_overdue: isOverdue,
          assigned_to: workOrder.model.assigned_to || '',
          description: workOrder.model.description || '',
          pm_template_id: workOrder.model.pm_template_id || null,
          pm_due_at: workOrder.model.pm_due_at || null
        }
      };
    }).filter(event => event !== null); // Remove null events

    this.calendar.removeAllEvents();
    this.calendar.addEventSource(this.calendarEvents);
  }

  private handleEventClick(info: any) {
    const event = info.event;
    const extendedProps = event.extendedProps;
    
    // Dispatch custom event with PM work order details
    this.dispatchEvent(new CustomEvent('pm-event-click', {
      detail: {
        workOrderId: extendedProps.work_order_id,
        assetId: extendedProps.asset_id,
        title: event.title,
        dueDate: event.start,
        priority: extendedProps.priority,
        status: extendedProps.status,
        isOverdue: extendedProps.is_overdue,
        assignedTo: extendedProps.assigned_to,
        description: extendedProps.description,
        pmTemplateId: extendedProps.pm_template_id,
        pmDueAt: extendedProps.pm_due_at
      },
      bubbles: true
    }));
  }

  private handleEventMount(info: any) {
    const event = info.event;
    const extendedProps = event.extendedProps;
    
    // Add tooltip or additional styling based on event properties
    if (extendedProps.is_overdue) {
      event.setProp('title', `${event.title} - OVERDUE`);
    }
  }

  private handleEventDrop(info: any) {
    const event = info.event;
    const newDate = event.start;
    const workOrderId = event.extendedProps.work_order_id;
    
    console.log('PM work order dropped to new date:', {
      workOrderId,
      newDate,
      oldDate: info.oldEvent.start
    });
    
    // Dispatch event to parent component to update the work order
    this.dispatchEvent(new CustomEvent('pm-event-moved', {
      detail: {
        workOrderId,
        newPmDueAt: newDate.getTime(),
        oldPmDueAt: info.oldEvent.start.getTime()
      },
      bubbles: true
    }));
  }

  private handleEventResize(info: any) {
    const event = info.event;
    const newEndDate = event.end;
    const workOrderId = event.extendedProps.work_order_id;
    
    console.log('PM work order resized:', {
      workOrderId,
      newEndDate,
      oldEndDate: info.oldEvent.end
    });
    
    // For PM work orders, we typically don't resize but if needed, we can handle it here
    // For now, just dispatch the event
    this.dispatchEvent(new CustomEvent('pm-event-resized', {
      detail: {
        workOrderId,
        newPmDueAt: newEndDate.getTime(),
        oldPmDueAt: info.oldEvent.end.getTime()
      },
      bubbles: true
    }));
  }

  private handleResize = () => {
    if (this.calendar) {
      // Force calendar to recalculate its size
      setTimeout(() => {
        this.calendar?.updateSize();
      }, 100);
    }
  };

  render() {
    return html`
      <div id="calendar"></div>
    `;
  }
}
